/**
 * Persist FileSystemDirectoryHandle per (userId, sessionId) in IndexedDB.
 * IndexedDB is the only storage that supports structured-cloneable handles.
 *
 * Keys are prefixed with the current user id so handles persisted by one
 * account can't be read by another on a shared machine. `clearForUser`
 * wipes everything under a prefix and is called on login/logout.
 */

const DB_NAME = "strands-handles";
const STORE = "session-handles";
// v2 wiped legacy leaks from the pre-disconnect-on-switch era.
// v3 wiped a second wave of leaks from a render race where the persist effect
// saved docs.dirHandle under the new activeId before useDocSync's disconnect
// setState committed.
// v4 migrates to user-prefixed keys ("${userId}:${sessionId}"). Any pre-v4
// entries without a prefix are dropped on upgrade so cross-user data can't
// survive.
const DB_VERSION = 4;

function key(userId: string, sessionId: string): string {
  return `${userId}:${sessionId}`;
}

/**
 * Read the current user id from localStorage. Callers run outside React
 * (mutations, cleanup effects) so threading `useAuth()` through would add
 * more plumbing than it's worth. Returns null if nobody is logged in — in
 * that case handle ops should just no-op.
 */
export function currentUserId(): string | null {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    const u = JSON.parse(raw) as { id?: string };
    return u.id ?? null;
  } catch {
    return null;
  }
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (db.objectStoreNames.contains(STORE)) db.deleteObjectStore(STORE);
      db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function saveHandle(userId: string, sessionId: string, handle: FileSystemDirectoryHandle): Promise<void> {
  const db = await open();
  const tx = db.transaction(STORE, "readwrite");
  tx.objectStore(STORE).put(handle, key(userId, sessionId));
  await new Promise<void>((res, rej) => { tx.oncomplete = () => res(); tx.onerror = () => rej(tx.error); });
}

export async function loadHandle(userId: string, sessionId: string): Promise<FileSystemDirectoryHandle | null> {
  const db = await open();
  const tx = db.transaction(STORE, "readonly");
  const req = tx.objectStore(STORE).get(key(userId, sessionId));
  return new Promise((res, rej) => {
    req.onsuccess = () => res((req.result as FileSystemDirectoryHandle | undefined) ?? null);
    req.onerror = () => rej(req.error);
  });
}

export async function removeHandle(userId: string, sessionId: string): Promise<void> {
  const db = await open();
  const tx = db.transaction(STORE, "readwrite");
  tx.objectStore(STORE).delete(key(userId, sessionId));
  await new Promise<void>((res, rej) => { tx.oncomplete = () => res(); tx.onerror = () => rej(tx.error); });
}

/** Drop every handle belonging to `userId`. Called on logout. */
export async function clearForUser(userId: string): Promise<void> {
  const db = await open();
  const tx = db.transaction(STORE, "readwrite");
  const store = tx.objectStore(STORE);
  const prefix = `${userId}:`;
  await new Promise<void>((res, rej) => {
    const req = store.openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) { res(); return; }
      if (typeof cursor.key === "string" && cursor.key.startsWith(prefix)) {
        cursor.delete();
      }
      cursor.continue();
    };
    req.onerror = () => rej(req.error);
  });
}
